// The visual special-rules overlay (MapDef.ruleOverlay): data shape and the
// pure resolvers the app and the engine tests share.
//
// A map declares SETS (which territories or regions a rule is about, with the
// glyph and colour they have in the art) and RULES (typed facts about a set).
// The app draws every fact the same way on every board; nothing in the
// renderer knows about a particular map. Link pairs and reach targets are not
// hand-listed: they are derived from the map's `adjacencies` and
// `oneWayBorders`, and an engine test checks every dice, bonus and cap fact
// against the map data, so the overlay cannot drift from the rules.
//
// `MapDef.specialRules` (plain text) stays the screen-reader fallback.

/** The visual special-rules overlay. Optional: maps without it show no ★ entry. */
export interface RuleOverlay {
  /** The shapes the rules talk about. */
  sets: RuleSet[]
  /** One legend row each, in reading order. */
  rules: RuleCard[]
  /** Board-wide rules with no territory. */
  chips?: BoardChip[]
}

export interface RuleSet {
  id: string
  /** Short name ('Tower'): the only text a legend row needs. */
  label: string
  /** The shape as drawn in the art. */
  glyph: RuleGlyph
  /** Highlight tint, taken from the art. */
  color: string
  /** Territories cut out of the scrim and outlined. */
  territories?: TerritorySelector
  /** Whole regions outlined (mesas, islands). Their territories are members too. */
  regions?: TerritorySelector
}

/** Picks territories (or regions) from the map data. Every given field must match. */
export interface TerritorySelector {
  ids?: string[]
  idPrefix?: string
  idSuffix?: string
  notIdSuffix?: string
  /** The name ends with one of these words ('Iron Fort' matches 'Fort'). */
  nameSuffix?: string[]
  /** The name starts with none of these. */
  notNamePrefix?: string[]
  /** Territories only: no region. */
  noRegion?: boolean
}

export type RuleGlyph =
  | 'cross' | 'starburst' | 'octagon' | 'hex' | 'hexcore' | 'swatch' | 'grass'
  | 'tower' | 'keep' | 'flag' | 'gate' | 'wall' | 'cannon'
  | 'island' | 'islet' | 'landing' | 'ring' | 'sand'

export type DieSides = 4 | 5 | 6 | 7 | 8 | 9

export type RuleFact =
  /** A region bonus. `set` (default: the rule's set) names whose regions pay it. */
  | { kind: 'bonus'; value: number; label?: string; set?: string }
  /** The set's members defend with this die (against `vs` only, when given). */
  | { kind: 'defend'; die: DieSides; vs?: string }
  /** The set's members attack with this die on every border. */
  | { kind: 'attack'; die: DieSides }
  /** Attacks from `from` into `to` roll these dice. */
  | { kind: 'matchup'; from: string; to: string; attack: DieSides; defend: DieSides; example?: [string, string]; drawAll?: boolean }
  /** One-way borders reaching `steps` away (into `to`, when given). */
  /**
   * `tagAt`: where the example's reach tag sits on the board (open ground, e.g.
   * a lava pool), joined to the example by a leader line. Absent: the board
   * layer finds a clear spot beside it.
   */
  | { kind: 'reach'; to?: string; steps: number | 'area'; oneWay: boolean; attack?: DieSides; example?: string; drawAll?: boolean; tagAt?: { x: number; y: number } }
  /** Borders between sets: every pair, the one-way borders, or the two-way adjacencies. */
  | { kind: 'link'; from: string; to?: string; pairs: 'all' | 'oneWay' | 'adjacent'; style: 'warp' | 'oneway' | 'sea'; dice?: { attack: DieSides; defend: DieSides }; label?: string }
  /** A troop cap. */
  | { kind: 'cap'; max: number }
  /** Pays no bonus. */
  | { kind: 'noBonus' }

export interface RuleCard {
  id: string
  /** Row name when it differs from the set's label. */
  label?: string
  set: string
  /** Further sets lit by this row (shown when the row is isolated). */
  also?: string[]
  facts: RuleFact[]
}

export interface BoardChip {
  icon: 'cards' | 'cap' | 'none' | 'clock'
  label: string
}

// ─── Resolution ─────────────────────────────────────────────────────────────

/** The slice of a map the resolvers read. */
export interface RuleOverlayMap {
  territories: ReadonlyArray<{ id: string; name: string; regionId?: string; adjacencies: readonly string[]; oneWayBorders?: readonly string[] }>
  regions: ReadonlyArray<{ id: string; name: string }>
}

export interface ResolvedRuleSet {
  /** Territories picked by the set's `territories` selector. */
  territoryIds: string[]
  /** Regions picked by the set's `regions` selector. */
  regionIds: string[]
  /** Every territory in the set: the picked ones plus those inside picked regions. */
  memberIds: string[]
}

function matches(item: { id: string; name: string; regionId?: string }, q: TerritorySelector): boolean {
  if (q.ids && !q.ids.includes(item.id)) return false
  if (q.idSuffix && !item.id.endsWith(q.idSuffix)) return false
  if (q.idPrefix && !item.id.startsWith(q.idPrefix)) return false
  if (q.notIdSuffix && item.id.endsWith(q.notIdSuffix)) return false
  if (q.noRegion && item.regionId) return false
  if (q.nameSuffix && !q.nameSuffix.some((s) => item.name === s || item.name.endsWith(` ${s}`))) return false
  if (q.notNamePrefix && q.notNamePrefix.some((p) => item.name.startsWith(p))) return false
  return true
}

/** Resolve one set against the map data. */
export function resolveRuleSet(map: RuleOverlayMap, set: RuleSet): ResolvedRuleSet {
  const territoryIds = set.territories ? map.territories.filter((t) => matches(t, set.territories!)).map((t) => t.id) : []
  const regionIds = set.regions ? map.regions.filter((r) => matches(r, set.regions!)).map((r) => r.id) : []
  const members = new Set(territoryIds)
  if (regionIds.length) {
    const picked = new Set(regionIds)
    for (const t of map.territories) if (t.regionId && picked.has(t.regionId)) members.add(t.id)
  }
  return { territoryIds, regionIds, memberIds: [...members] }
}

/** Every set of an overlay, resolved, by set id. */
export function resolveRuleSets(map: RuleOverlayMap, overlay: RuleOverlay): Record<string, ResolvedRuleSet> {
  const out: Record<string, ResolvedRuleSet> = {}
  for (const set of overlay.sets) out[set.id] = resolveRuleSet(map, set)
  return out
}

/**
 * The territory pairs a link fact connects, derived from the map:
 * `all` = every member of `from` with every member of `to`; `oneWay` = the
 * one-way borders out of `from` (into `to`, when given); `adjacent` = the
 * two-way adjacencies out of `from` (into `to`, when given). One-way links
 * keep their direction; the others are listed once per unordered pair.
 */
export function ruleLinkPairs(
  map: RuleOverlayMap,
  fact: Extract<RuleFact, { kind: 'link' }>,
  sets: Record<string, ResolvedRuleSet>,
): Array<[string, string]> {
  const byId = new Map(map.territories.map((t) => [t.id, t]))
  const from = sets[fact.from]?.memberIds ?? []
  const to = fact.to ? new Set(sets[fact.to]?.memberIds ?? []) : null
  const directed = fact.pairs === 'oneWay' || fact.style === 'oneway'
  const out: Array<[string, string]> = []
  const seen = new Set<string>()
  for (const a of from) {
    const t = byId.get(a)
    if (!t) continue
    const candidates = fact.pairs === 'all' ? [...(to ?? [])] : fact.pairs === 'oneWay' ? (t.oneWayBorders ?? []) : t.adjacencies
    for (const b of candidates) {
      if (b === a || (to && !to.has(b))) continue
      const key = directed ? `${a}>${b}` : [a, b].sort().join('|')
      if (seen.has(key)) continue
      seen.add(key)
      out.push([a, b])
    }
  }
  return out
}

/** One-way reach targets of `sourceId` (inside `toSet`, when given). */
export function ruleReachTargets(map: RuleOverlayMap, sourceId: string, toSet?: ResolvedRuleSet): string[] {
  const source = map.territories.find((t) => t.id === sourceId)
  const targets = source?.oneWayBorders ?? []
  if (!toSet) return [...targets]
  const ok = new Set(toSet.memberIds)
  return targets.filter((id) => ok.has(id))
}

/**
 * Every border from a member of `from` into a member of `to` (two-way or
 * one-way), as [attacker, defender] pairs.
 */
export function ruleMatchupPairs(
  map: RuleOverlayMap,
  fromSet: ResolvedRuleSet,
  toSet: ResolvedRuleSet,
): Array<[string, string]> {
  const byId = new Map(map.territories.map((t) => [t.id, t]))
  const to = new Set(toSet.memberIds)
  const out: Array<[string, string]> = []
  for (const a of fromSet.memberIds) {
    const t = byId.get(a)
    if (!t) continue
    for (const b of [...t.adjacencies, ...(t.oneWayBorders ?? [])]) if (to.has(b)) out.push([a, b])
  }
  return out
}
